<template>
  <div>
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <div v-if="job">
          <div class="job-title">{{ job.name }}</div>
          <div class="job-meta">
            <el-tag :type="statusType(job.status)" size="small">{{
              job.status
            }}</el-tag>
            <span class="meta-text"
              >Created {{ formatDate(job.createdAt) }}</span
            >
            <span class="meta-text" v-if="job.scheduledDate">
              Scheduled {{ formatDateShort(job.scheduledDate) }}
              <el-button
                v-if="
                  job.status === 'Draft' &&
                    $permission('Installation Tool.Jobs.Edit')
                "
                type="text"
                size="mini"
                icon="el-icon-edit"
                style="padding:0;margin-left:4px"
                @click="openEditSchedule"
              />
            </span>
            <span
              class="meta-text"
              v-else-if="
                job.status === 'Draft' &&
                  $permission('Installation Tool.Jobs.Edit')
              "
            >
              <el-button
                type="text"
                size="mini"
                style="padding:0;font-size:12px"
                @click="openEditSchedule"
                >+ Set scheduled date</el-button
              >
            </span>
            <span class="meta-text" v-if="job.submittedAt"
              >Submitted {{ formatDate(job.submittedAt) }}</span
            >
            <span class="meta-text" v-if="job.pqSignedOffAt"
              >PQ Signed off {{ formatDate(job.pqSignedOffAt) }}</span
            >
          </div>
        </div>
      </div>
      <div class="header-actions" v-if="job">
        <el-button
          v-if="$permission('Installation Tool.Jobs.Edit')"
          size="small"
          icon="el-icon-warning-outline"
          @click="showIssueDialog = true"
          >Report Issue</el-button
        >
        <el-button
          v-if="
            job.status === 'Draft' &&
              $permission('Installation Tool.Jobs.Delete')
          "
          type="danger"
          size="small"
          icon="el-icon-delete"
          plain
          :loading="deleting"
          @click="deleteJob"
          >Delete</el-button
        >
        <el-button
          v-if="
            job.status === 'Draft' && $permission('Installation Tool.Jobs.Edit')
          "
          type="success"
          icon="el-icon-upload"
          :loading="publishing"
          @click="publishJob"
          :disabled="!canPublish"
          >Publish Job</el-button
        >
        <el-tooltip
          v-if="job.status === 'Draft' && !canPublish"
          content="Assign at least 1 installer before publishing"
          placement="top"
        >
          <i class="el-icon-question ml-8 text-muted" />
        </el-tooltip>
      </div>
    </div>

    <div v-if="job" v-loading="loading">
      <!-- Qualification summary -->
      <div class="qual-summary" v-if="job.qualificationSummary">
        <div class="qual-card">
          <div class="qual-card__num">
            {{ job.qualificationSummary.totalDevices }}
          </div>
          <div class="qual-card__label">Total Devices</div>
        </div>
        <div class="qual-card">
          <div
            class="qual-card__num"
            :class="
              job.qualificationSummary.iqPassed ===
                job.qualificationSummary.totalDevices &&
              job.qualificationSummary.totalDevices > 0
                ? 'text-success'
                : ''
            "
          >
            {{ job.qualificationSummary.iqPassed }}
          </div>
          <div class="qual-card__label">IQ Passed</div>
        </div>
        <div class="qual-card">
          <div
            class="qual-card__num"
            :class="
              job.qualificationSummary.oqPassed ===
                job.qualificationSummary.totalDevices &&
              job.qualificationSummary.totalDevices > 0
                ? 'text-success'
                : ''
            "
          >
            {{ job.qualificationSummary.oqPassed }}
          </div>
          <div class="qual-card__label">OQ Passed</div>
        </div>
        <div class="qual-card">
          <div
            class="qual-card__num"
            :class="
              job.qualificationSummary.allIqOqComplete
                ? 'text-success'
                : 'text-warning'
            "
          >
            <i
              :class="
                job.qualificationSummary.allIqOqComplete
                  ? 'el-icon-circle-check'
                  : 'el-icon-time'
              "
            />
          </div>
          <div class="qual-card__label">All Complete</div>
        </div>
      </div>

      <!-- Publish warning -->
      <el-alert
        v-if="job.status === 'Draft' && !canPublish"
        type="warning"
        :closable="false"
        style="margin-bottom:16px"
        title="Assign at least one installer before publishing this job."
      />

      <el-tabs v-model="activeTab" class="lora-tabs">
        <!-- Gateways Tab -->
        <el-tab-pane
          :label="`Gateways (${job.gateways ? job.gateways.length : 0})`"
          name="gateways"
        >
          <div
            class="tab-actions"
            v-if="
              ['Draft', 'Published'].includes(job.status) &&
                $permission('Installation Tool.Devices.Edit')
            "
          >
            <el-button
              size="small"
              type="primary"
              icon="el-icon-plus"
              @click="showGatewayPicker = true"
            >
              Assign Gateway
            </el-button>
          </div>
          <el-table
            :data="job.gateways || []"
            border
            class="app-table"
            :max-height="tableHeight"
            empty-text="No gateways assigned"
          >
            <el-table-column
              prop="gatewayEui"
              label="Gateway EUI"
              width="200"
            />
            <el-table-column prop="model" label="Model" />
            <el-table-column
              prop="frequencyPlan"
              label="Freq Plan"
              width="130"
            />
            <el-table-column prop="supplier" label="Supplier" width="130" />
            <el-table-column
              prop="friendlyName"
              label="Friendly Name"
              min-width="160"
            >
              <template slot-scope="{ row }">{{
                row.friendlyName || '--'
              }}</template>
            </el-table-column>
            <el-table-column prop="status" label="Status" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="deviceStatusType(row.status)" size="small">{{
                  row.status
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="IQ" width="110">
              <template slot-scope="{ row }">
                <el-tag :type="iqOqStatusType(rowIq(row))" size="small">{{
                  rowIq(row) || 'Not started'
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="OQ" width="110">
              <template slot-scope="{ row }">
                <el-tag :type="iqOqStatusType(rowOq(row))" size="small">{{
                  rowOq(row) || 'Not started'
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="80" align="center">
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  @click="openQualDetail(row, 'Gateway')"
                >
                  <i class="el-icon-document" /> View
                </el-button>
              </template>
            </el-table-column>
            <el-table-column
              label=""
              width="70"
              align="center"
              v-if="
                ['Draft', 'Published'].includes(job.status) &&
                  $permission('Installation Tool.Devices.Edit')
              "
            >
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  class="text-danger"
                  @click="unassignGateway(row)"
                >
                  <i class="el-icon-delete" />
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- End Devices Tab -->
        <el-tab-pane
          :label="`End Devices (${job.endDevices ? job.endDevices.length : 0})`"
          name="endDevices"
        >
          <div
            class="tab-actions"
            v-if="
              ['Draft', 'Published'].includes(job.status) &&
                $permission('Installation Tool.Devices.Edit')
            "
          >
            <el-button
              size="small"
              type="primary"
              icon="el-icon-plus"
              @click="showEndDevicePicker = true"
            >
              Assign End Device
            </el-button>
          </div>
          <el-table
            :data="job.endDevices || []"
            border
            class="app-table"
            :max-height="tableHeight"
            empty-text="No end devices assigned"
          >
            <el-table-column prop="devEui" label="Dev EUI" width="200" />
            <el-table-column prop="model" label="Model" width="140" />
            <el-table-column prop="kind" label="Kind" width="120" />
            <el-table-column
              prop="frequencyPlan"
              label="Freq Plan"
              width="130"
            />
            <el-table-column prop="supplier" label="Supplier" width="120" />
            <el-table-column
              prop="friendlyName"
              label="Friendly Name"
              min-width="160"
            >
              <template slot-scope="{ row }">{{
                row.friendlyName || '--'
              }}</template>
            </el-table-column>
            <el-table-column prop="status" label="Status" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="deviceStatusType(row.status)" size="small">{{
                  row.status
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="IQ" width="110">
              <template slot-scope="{ row }">
                <el-tag :type="iqOqStatusType(rowIq(row))" size="small">{{
                  rowIq(row) || 'Not started'
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="OQ" width="110">
              <template slot-scope="{ row }">
                <el-tag :type="iqOqStatusType(rowOq(row))" size="small">{{
                  rowOq(row) || 'Not started'
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="80" align="center">
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  @click="openQualDetail(row, 'EndDevice')"
                >
                  <i class="el-icon-document" /> View
                </el-button>
              </template>
            </el-table-column>
            <el-table-column
              label=""
              width="70"
              align="center"
              v-if="
                ['Draft', 'Published'].includes(job.status) &&
                  $permission('Installation Tool.Devices.Edit')
              "
            >
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  class="text-danger"
                  @click="unassignEndDevice(row)"
                >
                  <i class="el-icon-delete" />
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- Smartz Pods Tab -->
        <el-tab-pane
          :label="`Smartz Pods (${job.bleDevices ? job.bleDevices.length : 0})`"
          name="bleDevices"
        >
          <div
            class="tab-actions"
            v-if="
              ['Draft', 'Published'].includes(job.status) &&
                $permission('Installation Tool.Devices.Edit')
            "
          >
            <el-button
              size="small"
              type="primary"
              icon="el-icon-plus"
              @click="showBleDialog = true"
              >Assign Smartz Pod</el-button
            >
          </div>
          <el-table
            :data="job.bleDevices || []"
            border
            class="app-table"
            :max-height="tableHeight"
            empty-text="No Smartz Pods assigned"
          >
            <el-table-column label="Pod ID" min-width="160">
              <template slot-scope="{ row }"
                ><span style="font-family:monospace">{{
                  row.deviceId
                }}</span></template
              >
            </el-table-column>
            <el-table-column prop="model" label="Model" width="140" />
            <el-table-column label="Status" width="120">
              <template slot-scope="{ row }"
                ><el-tag :type="deviceStatusType(row.status)" size="small">{{
                  row.status
                }}</el-tag></template
              >
            </el-table-column>
            <el-table-column label="IQ" width="80">
              <template
                ><el-tag type="info" size="small">N/A</el-tag></template
              >
            </el-table-column>
            <el-table-column label="OQ" width="80">
              <template
                ><el-tag type="info" size="small">N/A</el-tag></template
              >
            </el-table-column>
            <el-table-column label="" width="80" align="center">
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  @click="openQualDetail(row, 'BleDevice')"
                >
                  <i class="el-icon-document" /> View
                </el-button>
              </template>
            </el-table-column>
            <el-table-column
              label=""
              width="70"
              align="center"
              v-if="['Draft', 'Published'].includes(job.status)"
            >
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  class="text-danger"
                  @click="unassignBle(row)"
                  ><i class="el-icon-delete"
                /></el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- Nodes Tab -->
        <el-tab-pane
          :label="`Nodes (${job.nodes ? job.nodes.length : 0})`"
          name="nodes"
        >
          <div
            class="tab-actions"
            v-if="['Draft', 'Published'].includes(job.status)"
          >
            <el-button
              size="small"
              type="primary"
              icon="el-icon-plus"
              @click="showNodeDialog = true"
              >Assign Minew Node</el-button
            >
          </div>
          <el-table
            :data="job.nodes || []"
            border
            class="app-table"
            :max-height="tableHeight"
            empty-text="No nodes assigned"
          >
            <el-table-column label="Node ID" min-width="180">
              <template slot-scope="{ row }"
                ><span style="font-family:monospace">{{
                  row.nodeId
                }}</span></template
              >
            </el-table-column>
            <el-table-column label="Friendly Name" min-width="160">
              <template slot-scope="{ row }">{{
                row.friendlyName || '—'
              }}</template>
            </el-table-column>
            <el-table-column prop="model" label="Model" width="140" />
            <el-table-column label="Status" width="120">
              <template slot-scope="{ row }"
                ><el-tag :type="deviceStatusType(row.status)" size="small">{{
                  row.status
                }}</el-tag></template
              >
            </el-table-column>
            <el-table-column label="IQ" width="110">
              <template slot-scope="{ row }"
                ><el-tag :type="iqOqStatusType(rowIq(row))" size="small">{{
                  rowIq(row) || 'Not started'
                }}</el-tag></template
              >
            </el-table-column>
            <el-table-column label="OQ" width="110">
              <template slot-scope="{ row }"
                ><el-tag :type="iqOqStatusType(rowOq(row))" size="small">{{
                  rowOq(row) || 'Not started'
                }}</el-tag></template
              >
            </el-table-column>
            <el-table-column label="" width="80" align="center">
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  @click="openQualDetail(row, 'Node')"
                >
                  <i class="el-icon-document" /> View
                </el-button>
              </template>
            </el-table-column>
            <el-table-column
              label=""
              width="70"
              align="center"
              v-if="['Draft', 'Published'].includes(job.status)"
            >
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  class="text-danger"
                  @click="unassignNode(row)"
                  ><i class="el-icon-delete"
                /></el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- Dashboard Displays Tab -->
        <el-tab-pane
          :label="
            `Displays (${
              job.dashboardDisplays ? job.dashboardDisplays.length : 0
            })`
          "
          name="displays"
        >
          <div
            class="tab-actions"
            v-if="['Draft', 'Published'].includes(job.status)"
          >
            <el-button
              size="small"
              type="primary"
              icon="el-icon-plus"
              @click="showDisplayDialog = true"
              >Assign Display</el-button
            >
          </div>
          <el-table
            :data="job.dashboardDisplays || []"
            border
            class="app-table"
            :max-height="tableHeight"
            empty-text="No dashboard displays assigned"
          >
            <el-table-column label="Identifier" min-width="160">
              <template slot-scope="{ row }"
                ><span style="font-family:monospace">{{
                  row.deviceIdentifier
                }}</span></template
              >
            </el-table-column>
            <el-table-column label="Location / Friendly Name" min-width="180">
              <template slot-scope="{ row }">{{
                row.locationName || row.friendlyName || '—'
              }}</template>
            </el-table-column>
            <el-table-column prop="model" label="Model" width="160" />
            <el-table-column label="Status" width="120">
              <template slot-scope="{ row }"
                ><el-tag :type="deviceStatusType(row.status)" size="small">{{
                  row.status
                }}</el-tag></template
              >
            </el-table-column>
            <el-table-column label="IQ" width="110">
              <template slot-scope="{ row }"
                ><el-tag :type="iqOqStatusType(rowIq(row))" size="small">{{
                  rowIq(row) || 'Not started'
                }}</el-tag></template
              >
            </el-table-column>
            <el-table-column label="OQ" width="110">
              <template slot-scope="{ row }"
                ><el-tag :type="iqOqStatusType(rowOq(row))" size="small">{{
                  rowOq(row) || 'Not started'
                }}</el-tag></template
              >
            </el-table-column>
            <el-table-column label="" width="80" align="center">
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  @click="openQualDetail(row, 'DashboardDisplay')"
                >
                  <i class="el-icon-document" /> View
                </el-button>
              </template>
            </el-table-column>
            <el-table-column
              label=""
              width="70"
              align="center"
              v-if="['Draft', 'Published'].includes(job.status)"
            >
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="small"
                  class="text-danger"
                  @click="unassignDisplay(row)"
                  ><i class="el-icon-delete"
                /></el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- Installers Tab -->
        <el-tab-pane
          :label="`Installers (${job.installers ? job.installers.length : 0})`"
          name="installers"
        >
          <div
            class="tab-actions"
            v-if="
              ['Draft', 'Published'].includes(job.status) &&
                $permission('Installation Tool.Users.Edit')
            "
          >
            <el-button
              size="small"
              type="primary"
              icon="el-icon-plus"
              @click="showInstallerPicker = true"
            >
              Assign Installer
            </el-button>
          </div>
          <div class="installer-list">
            <div
              v-for="inst in job.installers || []"
              :key="inst.userId"
              class="installer-card"
            >
              <div class="installer-card__avatar">
                {{ initials(inst.fullName || inst.email) }}
              </div>
              <div class="installer-card__info">
                <div class="installer-card__name">{{ inst.fullName }}</div>
                <div class="installer-card__email">{{ inst.email }}</div>
              </div>
              <el-button
                v-if="
                  ['Draft', 'Published'].includes(job.status) &&
                    $permission('Installation Tool.Users.Edit')
                "
                type="text"
                size="small"
                class="text-danger"
                @click="unassignInstaller(inst)"
                >Remove</el-button
              >
            </div>
            <div
              v-if="!job.installers || !job.installers.length"
              class="empty-hint"
            >
              No installers assigned. Add at least one to publish the job.
            </div>
          </div>
        </el-tab-pane>

        <!-- Issues Tab -->
        <el-tab-pane :label="`Issues (${issues.length})`" name="issues">
          <div class="tab-actions">
            <el-button
              size="small"
              type="warning"
              icon="el-icon-warning-outline"
              @click="showIssueDialog = true"
            >
              Report Issue
            </el-button>
          </div>
          <div v-loading="loadingIssues">
            <div
              v-if="!issues.length"
              class="empty-hint"
              style="padding:24px 0"
            >
              No issues reported for this job.
            </div>
            <div v-for="issue in issues" :key="issue.id" class="issue-card">
              <div class="issue-card__head">
                <el-tag
                  :type="issue.status === 'Open' ? 'danger' : 'success'"
                  size="small"
                  >{{ issue.status }}</el-tag
                >
                <span class="issue-card__category">{{
                  issueCategoryLabel(issue.category)
                }}</span>
                <span class="issue-card__date">{{
                  formatDate(issue.createdAt)
                }}</span>
                <el-button
                  v-if="issue.status === 'Open'"
                  size="mini"
                  type="text"
                  style="color:#67c23a"
                  :loading="resolvingId === issue.id"
                  @click="resolveIssue(issue)"
                  >Mark Resolved</el-button
                >
              </div>
              <div class="issue-card__desc">{{ issue.description }}</div>
              <div class="issue-card__meta">
                <span v-if="issue.reportedByRole"
                  >Reported by:
                  {{ issue.reportedByName || issue.reportedByUserId }} ({{
                    issue.reportedByRole
                  }})</span
                >
                <span v-if="issue.deviceType"
                  ><i class="el-icon-cpu" /> {{ issue.deviceType
                  }}<span v-if="issue.deviceId">
                    · {{ issue.deviceId }}</span
                  ></span
                >
              </div>
              <div
                class="issue-card__resolution"
                v-if="issue.status === 'Resolved' && issue.resolutionNotes"
              >
                <i class="el-icon-circle-check" style="color:#67c23a" />
                {{ issue.resolutionNotes }}
                <span v-if="issue.resolvedAt" class="issue-card__date">
                  · {{ formatDate(issue.resolvedAt) }}</span
                >
              </div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <!-- Device Qualification Detail Dialog -->
      <DeviceQualDialog ref="qualDialog" :job-id="jobId" />
    </div>

    <!-- Gateway Picker Dialog -->
    <el-dialog
      title="Assign Gateway"
      :visible.sync="showGatewayPicker"
      width="750px"
    >
      <div class="picker-filters">
        <el-input
          v-model="gatewaySearch"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="Search EUI, model or supplier"
          style="width:250px"
        />
        <el-tag type="info" size="small" style="margin-right:8px">
          <i class="el-icon-location-outline" /> Site requires:
          <strong>{{ siteFrequencyPlan || '...' }}</strong>
        </el-tag>
        <span class="picker-hint">Only matching devices are shown</span>
        <el-button
          size="small"
          icon="el-icon-refresh"
          @click="loadAvailableGateways"
          style="margin-left:auto"
        />
      </div>
      <el-table
        :data="filteredAvailableGateways"
        v-loading="loadingPool"
        border
        size="small"
        max-height="400"
        @selection-change="selectedGateways = $event"
      >
        <el-table-column type="selection" width="45" />
        <el-table-column prop="gatewayEui" label="Gateway EUI" width="190" />
        <el-table-column prop="model" label="Model" width="130" />
        <el-table-column prop="supplier" label="Supplier" width="120" />
        <el-table-column label="Friendly Name (optional)" min-width="200">
          <template slot-scope="{ row }">
            <el-input
              v-model="row.friendlyName"
              size="mini"
              placeholder="e.g. Main Roof Gateway"
            />
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="save-btn"
          :loading="assigning"
          :disabled="!selectedGateways.length"
          @click="assignGateways"
          >ASSIGN {{ selectedGateways.length }} SELECTED</el-button
        >
        <el-button class="cancel-btn" @click="showGatewayPicker = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- End Device Picker Dialog -->
    <el-dialog
      title="Assign End Device"
      :visible.sync="showEndDevicePicker"
      width="820px"
    >
      <div class="picker-filters">
        <el-input
          v-model="endDeviceSearch"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="Search EUI, model or supplier"
          style="width:250px"
        />
        <el-tag type="info" size="small" style="margin-right:8px">
          <i class="el-icon-location-outline" /> Site requires:
          <strong>{{ siteFrequencyPlan || '...' }}</strong>
        </el-tag>
        <el-select
          v-model="pickerKindFilter"
          placeholder="All Kinds"
          clearable
          size="small"
          @change="loadAvailableEndDevices"
          style="width:140px"
        >
          <el-option label="Bridge" value="Bridge" />
          <el-option label="LoraBleDevice" value="LoraBleDevice" />
        </el-select>
        <span class="picker-hint">Only matching devices are shown</span>
        <el-button
          size="small"
          icon="el-icon-refresh"
          @click="loadAvailableEndDevices"
          style="margin-left:auto"
        />
      </div>
      <el-table
        :data="filteredAvailableEndDevices"
        v-loading="loadingPool"
        border
        size="small"
        max-height="400"
        @selection-change="selectedEndDevices = $event"
      >
        <el-table-column type="selection" width="45" />
        <el-table-column prop="devEui" label="Dev EUI" width="190" />
        <el-table-column prop="model" label="Model" width="120" />
        <el-table-column prop="kind" label="Kind" width="110" />
        <el-table-column prop="supplier" label="Supplier" width="110" />
        <el-table-column label="Friendly Name (optional)" min-width="220">
          <template slot-scope="{ row }">
            <el-input
              v-model="row.friendlyName"
              size="mini"
              placeholder="e.g. Cold Room Sensor"
            />
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="save-btn"
          :loading="assigning"
          :disabled="!selectedEndDevices.length"
          @click="assignEndDevices"
          >ASSIGN {{ selectedEndDevices.length }} SELECTED</el-button
        >
        <el-button class="cancel-btn" @click="showEndDevicePicker = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Smartz Pod Pool Picker -->
    <el-dialog
      title="Assign Smartz Pod"
      :visible.sync="showBleDialog"
      width="720px"
    >
      <div class="picker-filters">
        <el-input
          v-model="bleSearch"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="Search serial number or model"
        />
        <span class="picker-hint">Unassigned pool devices only</span>
      </div>
      <el-table
        :data="filteredAvailableBleDevices"
        v-loading="loadingPool"
        border
        size="small"
        max-height="400"
        @selection-change="selectedBleDevices = $event"
      >
        <el-table-column type="selection" width="45" />
        <el-table-column label="Pod ID" min-width="210">
          <template slot-scope="{ row }">{{
            row.deviceId || row.serialNumber || '--'
          }}</template>
        </el-table-column>
        <el-table-column prop="model" label="Model" min-width="150" />
        <el-table-column label="Friendly Name (optional)" min-width="220">
          <template slot-scope="{ row }"
            ><el-input
              v-model="row.friendlyName"
              size="mini"
              placeholder="Friendly name"
          /></template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="save-btn"
          :loading="assigning"
          :disabled="!selectedBleDevices.length"
          @click="assignBleDevices"
          >ASSIGN {{ selectedBleDevices.length }} SELECTED</el-button
        >
        <el-button class="cancel-btn" @click="showBleDialog = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Minew Node Pool Picker -->
    <el-dialog
      title="Assign Minew Node"
      :visible.sync="showNodeDialog"
      width="720px"
    >
      <div class="picker-filters">
        <el-input
          v-model="nodeSearch"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="Search node ID, friendly name or model"
        /><span class="picker-hint">Unassigned pool devices only</span>
      </div>
      <el-table
        :data="filteredAvailableNodes"
        v-loading="loadingPool"
        border
        size="small"
        max-height="400"
        @selection-change="selectedNodes = $event"
      >
        <el-table-column type="selection" width="45" />
        <el-table-column prop="nodeId" label="Node ID" min-width="220" />
        <el-table-column prop="model" label="Model" min-width="140" />
        <el-table-column label="Friendly Name (optional)" min-width="220"
          ><template slot-scope="{ row }"
            ><el-input
              v-model="row.friendlyName"
              size="mini"
              placeholder="Friendly name"/></template
        ></el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="save-btn"
          :loading="assigning"
          :disabled="!selectedNodes.length"
          @click="assignNodes"
          >ASSIGN {{ selectedNodes.length }} SELECTED</el-button
        >
        <el-button class="cancel-btn" @click="showNodeDialog = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Dashboard Display Pool Picker -->
    <el-dialog
      title="Assign Dashboard Display"
      :visible.sync="showDisplayDialog"
      width="760px"
    >
      <div class="picker-filters">
        <el-input
          v-model="displaySearch"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="Search identifier, friendly name, location or model"
        /><span class="picker-hint">Unassigned pool devices only</span>
      </div>
      <el-table
        :data="filteredAvailableDisplays"
        v-loading="loadingPool"
        border
        size="small"
        max-height="400"
        @selection-change="selectedDisplays = $event"
      >
        <el-table-column type="selection" width="45" />
        <el-table-column
          prop="deviceIdentifier"
          label="Identifier"
          min-width="180"
        />
        <el-table-column
          prop="identifierType"
          label="Identifier Type"
          width="140"
        />
        <el-table-column prop="locationName" label="Location" min-width="160" />
        <el-table-column prop="model" label="Model" min-width="140" />
        <el-table-column label="Friendly Name (optional)" min-width="190"
          ><template slot-scope="{ row }"
            ><el-input
              v-model="row.friendlyName"
              size="mini"
              placeholder="Friendly name"/></template
        ></el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="save-btn"
          :loading="assigning"
          :disabled="!selectedDisplays.length"
          @click="assignDisplays"
          >ASSIGN {{ selectedDisplays.length }} SELECTED</el-button
        >
        <el-button class="cancel-btn" @click="showDisplayDialog = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Installer Picker Dialog -->
    <el-dialog
      title="Assign Installer"
      :visible.sync="showInstallerPicker"
      width="580px"
    >
      <el-tabs v-model="installerDialogTab">
        <el-tab-pane label="Existing Installers" name="existing">
          <el-table
            :data="availableInstallers"
            v-loading="loadingInstallers"
            border
            size="small"
            max-height="320"
            @selection-change="selectedInstallers = $event"
          >
            <el-table-column type="selection" width="45" />
            <el-table-column prop="fullName" label="Name" width="160" />
            <el-table-column prop="email" label="Email" />
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="Create New Installer" name="create">
          <el-form :model="newInstallerForm" label-width="100px">
            <el-form-item label="Full Name" required>
              <el-input
                v-model="newInstallerForm.fullName"
                placeholder="e.g. Jordan Smith"
              />
            </el-form-item>
            <el-form-item label="Email" required>
              <el-input
                v-model="newInstallerForm.email"
                placeholder="installer@example.com"
              />
            </el-form-item>
            <el-alert
              type="info"
              :closable="false"
              show-icon
              title="A temporary password will be emailed to the installer automatically."
            />
          </el-form>
        </el-tab-pane>
      </el-tabs>
      <span slot="footer" class="dialog-footer">
        <template v-if="installerDialogTab === 'existing'">
          <el-button
            class="save-btn"
            :loading="assigning"
            :disabled="!selectedInstallers.length"
            @click="assignInstallers"
            >ASSIGN {{ selectedInstallers.length }} SELECTED</el-button
          >
        </template>
        <template v-else>
          <el-button
            class="save-btn"
            :loading="creatingInstaller"
            :disabled="!newInstallerForm.email || !newInstallerForm.fullName"
            @click="createAndInviteInstaller"
            >CREATE &amp; ASSIGN</el-button
          >
        </template>
        <el-button class="cancel-btn" @click="showInstallerPicker = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Report Issue Dialog -->
    <el-dialog
      title="Report Issue"
      :visible.sync="showIssueDialog"
      width="500px"
      @close="resetIssueForm"
    >
      <el-form :model="issueForm" label-width="110px">
        <el-form-item label="Category" required>
          <el-select
            v-model="issueForm.category"
            placeholder="Select category"
            style="width:100%"
          >
            <el-option
              v-for="c in issueCategories"
              :key="c.value"
              :label="c.label"
              :value="c.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Description" required>
          <el-input
            type="textarea"
            :rows="4"
            v-model="issueForm.description"
            placeholder="Describe the issue in detail..."
          />
        </el-form-item>
        <el-form-item label="Device">
          <el-select
            v-model="issueForm.selectedDevice"
            placeholder="Optional — select a device"
            clearable
            style="width:100%"
            value-key="deviceId"
          >
            <el-option-group
              v-for="group in jobDeviceGroups"
              :key="group.label"
              :label="group.label"
            >
              <el-option
                v-for="d in group.devices"
                :key="d.deviceType + '-' + d.deviceId"
                :label="d.label"
                :value="d"
              />
            </el-option-group>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="save-btn"
          :loading="submittingIssue"
          :disabled="!issueForm.category || !issueForm.description"
          @click="submitIssue"
          >SUBMIT ISSUE</el-button
        >
        <el-button class="cancel-btn" @click="showIssueDialog = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Edit Scheduled Date Dialog -->
    <el-dialog
      title="Set Scheduled Date"
      :visible.sync="showScheduleDialog"
      width="400px"
    >
      <el-date-picker
        v-model="scheduleDateForm.date"
        type="date"
        placeholder="Pick a date"
        style="width:100%"
      />
      <span slot="footer" class="dialog-footer">
        <el-button
          class="save-btn"
          :loading="savingSchedule"
          @click="saveScheduledDate"
          >SAVE</el-button
        >
        <el-button class="cancel-btn" @click="showScheduleDialog = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import { loraError } from '@/utils/loraError'
import moment from 'moment'
import DeviceQualDialog from './DeviceQualDialog'

var ISSUE_CATEGORIES = [
  { value: 'SiteAccessDenied', label: 'Site Access Denied' },
  { value: 'DeviceDamaged', label: 'Device Damaged' },
  { value: 'DeviceMissing', label: 'Device Missing' },
  { value: 'WrongDeviceShipped', label: 'Wrong Device Shipped' },
  { value: 'PowerNotAvailable', label: 'Power Not Available' },
  {
    value: 'NetworkConnectivityProblem',
    label: 'Network Connectivity Problem'
  },
  {
    value: 'MountingLocationUnsuitable',
    label: 'Mounting Location Unsuitable'
  },
  { value: 'CustomerRequestedDelay', label: 'Customer Requested Delay' },
  { value: 'WeatherDelay', label: 'Weather Delay' },
  { value: 'SafetyHazard', label: 'Safety Hazard' },
  { value: 'Other', label: 'Other' }
]

export default {
  name: 'LoraJobDetail',
  components: { DeviceQualDialog },
  props: {
    jobId: { type: [String, Number], required: true }
  },
  computed: {
    tableHeight() {
      return window.innerHeight - 380
    },
    filteredAvailableGateways() {
      return this.filterPool(this.availableGateways, this.gatewaySearch)
    },
    filteredAvailableEndDevices() {
      return this.filterPool(this.availableEndDevices, this.endDeviceSearch)
    },
    filteredAvailableBleDevices() {
      return this.filterPool(this.availableBleDevices, this.bleSearch)
    },
    filteredAvailableNodes() {
      return this.filterPool(this.availableNodes, this.nodeSearch)
    },
    filteredAvailableDisplays() {
      return this.filterPool(this.availableDisplays, this.displaySearch)
    },
    canPublish() {
      return this.job && (this.job.installers || []).length > 0
    },
    jobDeviceGroups() {
      if (!this.job) return []
      var groups = [
        {
          label: 'Gateways',
          devices: (this.job.gateways || []).map(function(d) {
            return {
              label: d.friendlyName || d.gatewayEui,
              deviceType: 'Gateway',
              deviceId: d.id
            }
          })
        },
        {
          label: 'End Devices',
          devices: (this.job.endDevices || []).map(function(d) {
            return {
              label: d.friendlyName || d.devEui,
              deviceType: 'EndDevice',
              deviceId: d.id
            }
          })
        },
        {
          label: 'Smartz Pods',
          devices: (this.job.bleDevices || []).map(function(d) {
            return {
              label: d.deviceId,
              deviceType: 'BleDevice',
              deviceId: d.id
            }
          })
        },
        {
          label: 'Nodes',
          devices: (this.job.nodes || []).map(function(d) {
            return {
              label: d.friendlyName || d.nodeId,
              deviceType: 'Node',
              deviceId: d.id
            }
          })
        }
      ]
      return groups.filter(function(g) {
        return g.devices.length > 0
      })
    }
  },
  data() {
    return {
      job: null,
      siteFrequencyPlan: '',
      loading: false,
      publishing: false,
      deleting: false,
      assigning: false,
      activeTab: 'gateways',
      showGatewayPicker: false,
      gatewaySearch: '',
      availableGateways: [],
      selectedGateways: [],
      loadingPool: false,
      pickerKindFilter: '',
      showEndDevicePicker: false,
      endDeviceSearch: '',
      availableEndDevices: [],
      selectedEndDevices: [],
      showInstallerPicker: false,
      availableInstallers: [],
      selectedInstallers: [],
      loadingInstallers: false,
      installerDialogTab: 'existing',
      newInstallerForm: { email: '', fullName: '' },
      creatingInstaller: false,
      showBleDialog: false,
      bleSearch: '',
      availableBleDevices: [],
      selectedBleDevices: [],
      showNodeDialog: false,
      nodeSearch: '',
      availableNodes: [],
      selectedNodes: [],
      showDisplayDialog: false,
      displaySearch: '',
      availableDisplays: [],
      selectedDisplays: [],
      // Issues
      issues: [],
      loadingIssues: false,
      showIssueDialog: false,
      submittingIssue: false,
      resolvingId: null,
      issueForm: { category: '', description: '', selectedDevice: null },
      issueCategories: ISSUE_CATEGORIES,
      // Schedule
      showScheduleDialog: false,
      savingSchedule: false,
      scheduleDateForm: { date: null }
    }
  },
  mounted() {
    this.loadJob()
  },
  watch: {
    showGatewayPicker: function(val) {
      if (val) this.loadAvailableGateways()
    },
    showEndDevicePicker: function(val) {
      if (val) this.loadAvailableEndDevices()
    },
    showBleDialog: function(val) {
      if (val) this.loadAvailableBleDevices()
    },
    showNodeDialog: function(val) {
      if (val) this.loadAvailableNodes()
    },
    showDisplayDialog: function(val) {
      if (val) this.loadAvailableDisplays()
    },
    showInstallerPicker: function(val) {
      if (val) {
        this.installerDialogTab = 'existing'
        this.newInstallerForm = { email: '', fullName: '' }
        this.selectedInstallers = []
        this.loadAvailableInstallers()
      }
    },
    activeTab: function(val) {
      if (val === 'issues') this.loadIssues()
    }
  },
  methods: {
    filterPool(devices, query) {
      const normalized = String(query || '')
        .trim()
        .toLowerCase()
      if (!normalized) return devices
      return (devices || []).filter(device =>
        Object.keys(device || {}).some(key =>
          String(device[key] == null ? '' : device[key])
            .toLowerCase()
            .includes(normalized)
        )
      )
    },
    async loadJob() {
      this.loading = true
      try {
        var res = await LoraApiService.getJob(this.jobId)
        this.job = res.data
        this.loadIssues()
        if (this.job && this.job.siteId) {
          LoraApiService.getSite(this.job.siteId)
            .then(
              function(r) {
                this.siteFrequencyPlan = (r.data && r.data.frequencyPlan) || ''
              }.bind(this)
            )
            .catch(function() {})
        }
      } catch (e) {
        this.$message.error('Failed to load job')
      } finally {
        this.loading = false
      }
    },

    async publishJob() {
      try {
        await this.$confirm(
          'Publish this job? Installers will be able to see and start it.',
          'Confirm Publish',
          {
            type: 'warning',
            confirmButtonText: 'Publish'
          }
        )
      } catch {
        return
      }
      this.publishing = true
      try {
        await LoraApiService.publishJob(this.jobId)
        this.$message.success('Job published')
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to publish'))
      } finally {
        this.publishing = false
      }
    },

    async deleteJob() {
      try {
        await this.$confirm(
          'Delete this job permanently? This cannot be undone.',
          'Delete Job',
          {
            type: 'warning',
            confirmButtonText: 'Delete',
            confirmButtonClass: 'el-button--danger'
          }
        )
      } catch {
        return
      }
      this.deleting = true
      try {
        await LoraApiService.deleteJob(this.jobId)
        this.$message.success('Job deleted')
        this.$emit('back')
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to delete job'))
      } finally {
        this.deleting = false
      }
    },

    // ── Scheduled date ────────────────────────────────────────────────────
    openEditSchedule() {
      const raw = this.job && this.job.scheduledDate
      this.scheduleDateForm.date = raw ? new Date(raw) : null
      this.showScheduleDialog = true
    },
    async saveScheduledDate() {
      this.savingSchedule = true
      try {
        const d = this.scheduleDateForm.date
        await LoraApiService.updateJobSchedule(this.jobId, {
          scheduledDate: d ? moment(d).format('YYYY-MM-DDTHH:mm:ss') : null
        })
        this.$message.success('Scheduled date updated')
        this.showScheduleDialog = false
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to update scheduled date'))
      } finally {
        this.savingSchedule = false
      }
    },

    // ── Issues ────────────────────────────────────────────────────────────
    async loadIssues() {
      this.loadingIssues = true
      try {
        var res = await LoraApiService.getJobIssues(this.jobId)
        this.issues = res.data || []
      } catch (e) {
        this.$message.error('Failed to load issues')
      } finally {
        this.loadingIssues = false
      }
    },
    resetIssueForm() {
      this.issueForm = { category: '', description: '', selectedDevice: null }
    },
    async submitIssue() {
      if (!this.issueForm.category || !this.issueForm.description) {
        this.$message.warning('Category and description are required')
        return
      }
      this.submittingIssue = true
      try {
        var currentUser = JSON.parse(localStorage.getItem('user') || '{}')
        var body = {
          reportedByUserId:
            currentUser.id || currentUser.userId || currentUser.username,
          category: this.issueForm.category,
          description: this.issueForm.description
        }
        if (this.issueForm.selectedDevice) {
          body.deviceType = this.issueForm.selectedDevice.deviceType
          body.deviceId = this.issueForm.selectedDevice.deviceId
        }
        await LoraApiService.reportIssue(this.jobId, body)
        this.$message.success('Issue reported')
        this.showIssueDialog = false
        this.resetIssueForm()
        this.issues = []
        this.activeTab = 'issues'
        this.loadIssues()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to submit issue'))
      } finally {
        this.submittingIssue = false
      }
    },
    async resolveIssue(issue) {
      var promptResult
      try {
        promptResult = await this.$prompt('Resolution notes', 'Resolve Issue', {
          inputPlaceholder: 'Describe how this was resolved...',
          inputValidator: function(v) {
            return v && v.trim() ? true : 'Required'
          },
          confirmButtonText: 'Resolve',
          cancelButtonText: 'Cancel'
        })
      } catch {
        return
      }
      this.resolvingId = issue.id
      try {
        var currentUser = JSON.parse(localStorage.getItem('user') || '{}')
        await LoraApiService.resolveIssue(this.jobId, issue.id, {
          resolvedByUserId:
            currentUser.id || currentUser.userId || currentUser.username,
          resolutionNotes: promptResult.value
        })
        this.$message.success('Issue resolved')
        this.loadIssues()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to resolve issue'))
      } finally {
        this.resolvingId = null
      }
    },
    issueCategoryLabel(cat) {
      var found = ISSUE_CATEGORIES.filter(function(c) {
        return c.value === cat
      })[0]
      return found ? found.label : cat
    },

    // ── Gateway assignment ────────────────────────────────────────────────
    async loadAvailableGateways() {
      this.loadingPool = true
      try {
        var params = { status: 'Unassigned' }
        if (this.siteFrequencyPlan)
          params.frequencyPlan = this.siteFrequencyPlan
        var res = await LoraApiService.getGateways(params)
        this.availableGateways = (res.data || []).map(function(g) {
          return Object.assign({}, g, { friendlyName: '' })
        })
      } catch (e) {
        this.$message.error('Failed to load gateway pool')
      } finally {
        this.loadingPool = false
      }
    },
    async assignGateways() {
      this.assigning = true
      try {
        for (var i = 0; i < this.selectedGateways.length; i++) {
          var gw = this.selectedGateways[i]
          await LoraApiService.assignGateway(
            this.jobId,
            gw.id,
            gw.friendlyName || null
          )
        }
        this.$message.success(
          this.selectedGateways.length + ' gateway(s) assigned'
        )
        this.showGatewayPicker = false
        this.selectedGateways = []
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Assignment failed'))
      } finally {
        this.assigning = false
      }
    },
    async unassignGateway(gw) {
      try {
        await this.$confirm(
          'Unassign gateway ' + gw.gatewayEui + '?',
          'Confirm',
          { type: 'warning' }
        )
      } catch {
        return
      }
      try {
        await LoraApiService.unassignGateway(this.jobId, gw.id)
        this.$message.success('Gateway unassigned')
        this.loadJob()
      } catch (e) {
        this.$message.error('Failed to unassign gateway')
      }
    },

    // ── End device assignment ─────────────────────────────────────────────
    async loadAvailableEndDevices() {
      this.loadingPool = true
      try {
        var params = { status: 'Unassigned' }
        if (this.siteFrequencyPlan)
          params.frequencyPlan = this.siteFrequencyPlan
        if (this.pickerKindFilter) params.kind = this.pickerKindFilter
        var res = await LoraApiService.getEndDevices(params)
        this.availableEndDevices = (res.data || []).map(function(d) {
          return Object.assign({}, d, { friendlyName: '' })
        })
      } catch (e) {
        this.$message.error('Failed to load end device pool')
      } finally {
        this.loadingPool = false
      }
    },
    async assignEndDevices() {
      this.assigning = true
      try {
        for (var i = 0; i < this.selectedEndDevices.length; i++) {
          var dev = this.selectedEndDevices[i]
          await LoraApiService.assignEndDevice(
            this.jobId,
            dev.id,
            dev.friendlyName || null
          )
        }
        this.$message.success(
          this.selectedEndDevices.length + ' end device(s) assigned'
        )
        this.showEndDevicePicker = false
        this.selectedEndDevices = []
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Assignment failed'))
      } finally {
        this.assigning = false
      }
    },
    async unassignEndDevice(dev) {
      try {
        await this.$confirm(
          'Unassign end device ' + dev.devEui + '?',
          'Confirm',
          { type: 'warning' }
        )
      } catch {
        return
      }
      try {
        await LoraApiService.unassignEndDevice(this.jobId, dev.id)
        this.$message.success('End device unassigned')
        this.loadJob()
      } catch (e) {
        this.$message.error('Failed to unassign end device')
      }
    },

    // ── Additional pool assignment ────────────────────────────────────────
    poolItems(response) {
      const data = response && response.data
      return Array.isArray(data)
        ? data
        : data && Array.isArray(data.data)
        ? data.data
        : data && Array.isArray(data.items)
        ? data.items
        : data && Array.isArray(data.Items)
        ? data.Items
        : []
    },
    async loadAvailableBleDevices() {
      this.loadingPool = true
      try {
        const res = await LoraApiService.getBleDevices({
          status: 'Unassigned'
        })
        this.availableBleDevices = this.poolItems(res).map(device =>
          Object.assign({}, device, { friendlyName: '' })
        )
      } catch (e) {
        this.$message.error('Failed to load Smartz Pod pool')
      } finally {
        this.loadingPool = false
      }
    },
    async assignBleDevices() {
      this.assigning = true
      try {
        for (const device of this.selectedBleDevices) {
          await LoraApiService.assignBleDevice(this.jobId, {
            bleDeviceId: device.id,
            friendlyName: device.friendlyName || null
          })
        }
        this.$message.success(
          this.selectedBleDevices.length + ' Smartz Pod(s) assigned'
        )
        this.showBleDialog = false
        this.selectedBleDevices = []
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Assignment failed'))
      } finally {
        this.assigning = false
      }
    },
    async loadAvailableNodes() {
      this.loadingPool = true
      try {
        const res = await LoraApiService.getNodes({ status: 'Unassigned' })
        this.availableNodes = this.poolItems(res).map(device =>
          Object.assign({}, device, {
            friendlyName: device.friendlyName || ''
          })
        )
      } catch (e) {
        this.$message.error('Failed to load Minew Node pool')
      } finally {
        this.loadingPool = false
      }
    },
    async assignNodes() {
      this.assigning = true
      try {
        for (const device of this.selectedNodes) {
          await LoraApiService.assignNode(this.jobId, {
            nodeId: device.id,
            friendlyName: device.friendlyName || null
          })
        }
        this.$message.success(this.selectedNodes.length + ' node(s) assigned')
        this.showNodeDialog = false
        this.selectedNodes = []
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Assignment failed'))
      } finally {
        this.assigning = false
      }
    },
    async loadAvailableDisplays() {
      this.loadingPool = true
      try {
        const res = await LoraApiService.getDashboardDisplays({
          status: 'Unassigned'
        })
        this.availableDisplays = this.poolItems(res).map(device =>
          Object.assign({}, device, {
            friendlyName: device.friendlyName || ''
          })
        )
      } catch (e) {
        this.$message.error('Failed to load Dashboard Display pool')
      } finally {
        this.loadingPool = false
      }
    },
    async assignDisplays() {
      this.assigning = true
      try {
        for (const device of this.selectedDisplays) {
          await LoraApiService.assignDashboardDisplay(this.jobId, {
            displayId: device.id,
            friendlyName: device.friendlyName || null
          })
        }
        this.$message.success(
          this.selectedDisplays.length + ' display(s) assigned'
        )
        this.showDisplayDialog = false
        this.selectedDisplays = []
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Assignment failed'))
      } finally {
        this.assigning = false
      }
    },

    // ── BLE devices ───────────────────────────────────────────────────────
    async unassignBle(d) {
      try {
        await this.$confirm(
          'Remove Smartz Pod ' + (d.serialNumber || d.deviceId) + '?',
          'Confirm',
          { type: 'warning' }
        )
      } catch {
        return
      }
      try {
        await LoraApiService.unassignBleDevice(this.jobId, d.id)
        this.$message.success('Smartz Pod removed')
        this.loadJob()
      } catch (e) {
        this.$message.error('Failed to remove Smartz Pod')
      }
    },

    // ── Nodes ─────────────────────────────────────────────────────────────
    async unassignNode(n) {
      try {
        await this.$confirm('Remove node ' + n.nodeId + '?', 'Confirm', {
          type: 'warning'
        })
      } catch {
        return
      }
      try {
        await LoraApiService.unassignNode(this.jobId, n.id)
        this.$message.success('Node removed')
        this.loadJob()
      } catch (e) {
        this.$message.error('Failed to remove node')
      }
    },

    // ── Dashboard Displays ────────────────────────────────────────────────
    async unassignDisplay(d) {
      try {
        await this.$confirm(
          'Remove dashboard display ' + d.deviceIdentifier + '?',
          'Confirm',
          { type: 'warning' }
        )
      } catch {
        return
      }
      try {
        await LoraApiService.unassignDashboardDisplay(this.jobId, d.id)
        this.$message.success('Dashboard display removed')
        this.loadJob()
      } catch (e) {
        this.$message.error('Failed to remove dashboard display')
      }
    },

    // ── Installers ────────────────────────────────────────────────────────
    async loadAvailableInstallers() {
      this.loadingInstallers = true
      try {
        var res = await LoraApiService.getAvailableInstallers()
        var already = {}
        ;(this.job.installers || []).forEach(function(i) {
          already[i.userId] = true
        })
        this.availableInstallers = (res.data || []).filter(function(u) {
          return !already[u.userId]
        })
      } catch (e) {
        this.$message.error('Failed to load installers')
      } finally {
        this.loadingInstallers = false
      }
    },
    async assignInstallers() {
      this.assigning = true
      try {
        for (var i = 0; i < this.selectedInstallers.length; i++) {
          await LoraApiService.assignInstaller(
            this.jobId,
            this.selectedInstallers[i].userId
          )
        }
        this.$message.success(
          this.selectedInstallers.length + ' installer(s) assigned'
        )
        this.showInstallerPicker = false
        this.selectedInstallers = []
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Assignment failed'))
      } finally {
        this.assigning = false
      }
    },
    async createAndInviteInstaller() {
      this.creatingInstaller = true
      try {
        await LoraApiService.createAndInviteInstaller(this.jobId, {
          email: this.newInstallerForm.email,
          fullName: this.newInstallerForm.fullName
        })
        this.$message.success('Installer created — invite email sent')
        this.showInstallerPicker = false
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to create installer'))
      } finally {
        this.creatingInstaller = false
      }
    },
    async unassignInstaller(inst) {
      try {
        await this.$confirm(
          'Remove installer ' + (inst.fullName || inst.email) + '?',
          'Confirm',
          { type: 'warning' }
        )
      } catch {
        return
      }
      try {
        await LoraApiService.unassignInstaller(this.jobId, inst.userId)
        this.$message.success('Installer removed')
        this.loadJob()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to remove installer'))
      }
    },

    // ── Helpers ───────────────────────────────────────────────────────────
    formatDate(d) {
      return d ? moment(d).format('DD MMM YYYY HH:mm') : '-'
    },
    formatDateShort(d) {
      return d ? moment(d).format('DD MMM YYYY') : '-'
    },
    statusType(s) {
      return (
        {
          Draft: 'info',
          Published: 'primary',
          Submitted: 'warning',
          Complete: 'success',
          Escalated: 'danger'
        }[s] || 'info'
      )
    },
    iqOqStatusType(s) {
      return (
        { Pass: 'success', Fail: 'danger', InProgress: 'warning' }[s] || 'info'
      )
    },
    rowIq(row) {
      return row.iqStatus || null
    },
    rowOq(row) {
      return row.oqStatus || null
    },
    openQualDetail(row, deviceType) {
      this.$refs.qualDialog.open({
        deviceId: row.id,
        deviceType: deviceType,
        friendlyName: row.friendlyName || row.locationName || null,
        identifier:
          row.gatewayEui ||
          row.devEui ||
          row.nodeId ||
          row.deviceId ||
          row.deviceIdentifier
      })
    },
    deviceStatusType(s) {
      return (
        {
          Unassigned: 'info',
          Assigned: 'warning',
          Commissioned: 'success',
          Decommissioned: 'danger'
        }[s] || 'info'
      )
    },
    initials(str) {
      if (!str) return '?'
      return str
        .split(/[\s@]/)
        .map(function(w) {
          return w[0]
        })
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/varible.scss';

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}
.header-left {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.job-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a2332;
  margin: 0 0 8px;
}
.job-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  .meta-text {
    font-size: 13px;
    color: $font-color;
  }
  .meta-text + .meta-text::before {
    content: '·';
    margin-right: 10px;
    color: #c0c4cc;
  }
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.ml-8 {
  margin-left: 8px;
}
.text-muted {
  color: #c0c4cc;
}
.text-danger {
  color: #f56c6c;
}
.text-success {
  color: #67c23a;
}
.text-warning {
  color: #e6a23c;
}

.qual-summary {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}
.qual-card {
  flex: 1;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 14px 16px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  &__num {
    font-size: 24px;
    font-weight: 700;
    color: #1a2332;
    line-height: 1;
    margin-bottom: 6px;
  }
  &__label {
    font-size: 12px;
    color: $font-color;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.lora-tabs {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.tab-actions {
  margin-bottom: 12px;
}

.picker-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.picker-hint {
  font-size: 12px;
  color: #909399;
}

.section-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  min-height: 200px;
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-weight: 600;
    font-size: 14px;
    color: #1a2332;
  }
}

.add-form {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  &__actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding-top: 4px;
  }
}
.device-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  margin-bottom: 6px;
  &__main {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  &__id {
    font-family: monospace;
    font-size: 13px;
    font-weight: 500;
  }
  &__model {
    font-size: 12px;
    color: $font-color;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.installer-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.installer-card {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  gap: 12px;
  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #02a7f0;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
    flex-shrink: 0;
  }
  &__info {
    flex: 1;
  }
  &__name {
    font-weight: 600;
    font-size: 14px;
  }
  &__email {
    font-size: 12px;
    color: $font-color;
  }
}

// ── Issues ────────────────────────────────────────────────────────────────
.issue-card {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }
  &__category {
    font-weight: 600;
    font-size: 13px;
    color: #1a2332;
  }
  &__date {
    font-size: 12px;
    color: $font-color;
    margin-left: auto;
  }
  &__desc {
    font-size: 13px;
    color: #333;
    line-height: 1.6;
    margin-bottom: 6px;
  }
  &__meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: $font-color;
    margin-top: 6px;
  }
  &__resolution {
    font-size: 12px;
    color: #67c23a;
    margin-top: 6px;
  }
}

.empty-hint {
  color: #c0c4cc;
  font-size: 13px;
  padding: 12px 0;
}
</style>
